import type { ReactNode } from 'react';
import { Avatar, Box, Burger, Button as MantineButton, Group, NativeSelect, Text } from '@mantine/core';
import classes from './TopBar.module.css';

export interface TopBarProps {
  mobileNavOpen: boolean;
  onToggleMobileNav: () => void;
  burgerLabel: string;
  contextLabel: string;
  compactContextLabel?: string;
  showCompactContext?: boolean;
  showOrganizationSelect: boolean;
  showOutletSelect: boolean;
  organizationOptions: Array<{ value: string; label: string }>;
  outletOptions: Array<{ value: string; label: string }>;
  organizationValue: string;
  outletValue: string;
  onOrganizationChange: (value: string) => void;
  onOutletChange: (value: string) => void;
  organizationSelectDisabled: boolean;
  outletSelectDisabled: boolean;
  organizationSelectLabel: string;
  outletSelectLabel: string;
  noOutletsMessage?: ReactNode;
  locale: string;
  localeOptions: Array<{ value: string; label: string }>;
  onLocaleChange: (value: string) => void;
  languageAriaLabel: string;
  notifications: ReactNode;
  userDisplayName?: string;
  userRolesLabel?: string;
  logoutLabel: string;
  onLogout: () => void;
}

export function TopBar({
  mobileNavOpen,
  onToggleMobileNav,
  burgerLabel,
  contextLabel,
  compactContextLabel,
  showCompactContext,
  showOrganizationSelect,
  showOutletSelect,
  organizationOptions,
  outletOptions,
  organizationValue,
  outletValue,
  onOrganizationChange,
  onOutletChange,
  organizationSelectDisabled,
  outletSelectDisabled,
  organizationSelectLabel,
  outletSelectLabel,
  noOutletsMessage,
  locale,
  localeOptions,
  onLocaleChange,
  languageAriaLabel,
  notifications,
  userDisplayName,
  userRolesLabel,
  logoutLabel,
  onLogout,
}: TopBarProps) {
  return (
    <Group justify="space-between" h="100%" px="xl" className={classes.root} wrap="nowrap">
      <Group gap="sm" wrap="nowrap" align="center" style={{ minWidth: 0, flex: 1 }}>
        <Burger
          opened={mobileNavOpen}
          onClick={onToggleMobileNav}
          aria-label={burgerLabel}
          size="sm"
          hiddenFrom="md"
        />
        <Box data-testid="active-context-switcher" style={{ minWidth: 0 }}>
          <span className="visually-hidden" data-testid="active-context-label">{contextLabel}</span>
          {showOrganizationSelect || showOutletSelect ? (
            <Group gap="sm" wrap="wrap" align="center">
              {showOrganizationSelect ? (
                <NativeSelect
                  aria-label={organizationSelectLabel}
                  data-testid="active-context-organization-select"
                  size="sm"
                  value={organizationValue}
                  onChange={(event) => onOrganizationChange(event.currentTarget.value)}
                  data={organizationOptions}
                  disabled={organizationSelectDisabled}
                  style={{ minWidth: 160 }}
                />
              ) : null}
              {showOutletSelect ? (
                <NativeSelect
                  aria-label={outletSelectLabel}
                  data-testid="active-context-outlet-select"
                  size="sm"
                  value={outletValue}
                  onChange={(event) => onOutletChange(event.currentTarget.value)}
                  data={outletOptions}
                  disabled={outletSelectDisabled}
                  style={{ minWidth: 160 }}
                />
              ) : null}
            </Group>
          ) : showCompactContext && compactContextLabel ? (
            <Text className={classes.compactLabel} data-testid="active-context-compact">
              {compactContextLabel}
            </Text>
          ) : null}
          {noOutletsMessage ? (
            <Text className={classes.contextWarning} size="sm" data-testid="active-context-no-outlets">
              {noOutletsMessage}
            </Text>
          ) : null}
        </Box>
      </Group>

      <Group gap="sm" wrap="nowrap" align="center">
        <NativeSelect
          aria-label={languageAriaLabel}
          value={locale}
          onChange={(event) => onLocaleChange(event.currentTarget.value)}
          data={localeOptions}
          size="sm"
          w={72}
        />
        {notifications}
        {userDisplayName ? (
          <Group gap="xs" wrap="nowrap">
            <Avatar size={32} radius="xl" color="blue">{userDisplayName.slice(0, 1)}</Avatar>
            <Box visibleFrom="sm">
              <Text size="sm" fw={700} lh={1.2}>{userDisplayName}</Text>
              {userRolesLabel ? <Text size="xs" c="dimmed">{userRolesLabel}</Text> : null}
            </Box>
          </Group>
        ) : null}
        <MantineButton variant="light" color="blue" onClick={onLogout} data-testid="header-logout-button">
          {logoutLabel}
        </MantineButton>
      </Group>
    </Group>
  );
}
